import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Button,
  IconButton,
  Paper,
  Grid,
  Alert,
  Typography,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  TextField,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import { Add, Delete, Send, CheckCircle, Block, PostAdd, Undo, AttachFile } from '@mui/icons-material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import {
  useRouter,
  FormBottomBar,
  FormErrorDialog,
  useFormError,
  useNotification,
  EditableDataGrid,
  ServerFilteredEditCell,
  CommentsPanel,
  useTabHash,
} from '@viilutalous/common';
import { coaAPI, vatsAPI } from '@viilutalous/accounting';
import { useAuth } from '../../modules/auth/context/AuthContext';
import { employeesAPI } from '../employees/employeesAPI';
import { expensesAPI, categoriesAPI } from './expensesAPI';
import AttachmentDialog from './AttachmentDialog';
import ExpenseRowCard from './ExpenseRowCard';

const EXPENSE_STATE = {
  DRAFT: 0,
  DELETED: 1,
  SUBMITTED: 2,
  APPROVED: 3,
  POSTED: 4,
  PAID: 5,
  REFUSED: 6,
};

const workflowSteps = [
  { state: EXPENSE_STATE.DRAFT, label: 'draft' },
  { state: EXPENSE_STATE.SUBMITTED, label: 'submitted' },
  { state: EXPENSE_STATE.APPROVED, label: 'approved' },
  { state: EXPENSE_STATE.POSTED, label: 'posted' },
  { state: EXPENSE_STATE.PAID, label: 'paid' },
];

function getActiveStep(state) {
  if (state === EXPENSE_STATE.REFUSED) return -1;
  const idx = workflowSteps.findIndex(s => s.state === state);
  return idx >= 0 ? idx : 0;
}

export default function ExpenseForm({ tenantId, setBreadcrumbTitle }) {
  const { navigate, params } = useRouter();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showSuccess } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { error: formError, dialogOpen: errorDialogOpen, handleApiError, showError, closeDialog } = useFormError();
  const { id } = params;
  const isNew = id === 'new' || !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useTabHash(['rows', 'comments']);
  const [expenseState, setExpenseState] = useState(EXPENSE_STATE.DRAFT);
  const [actionLoading, setActionLoading] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [vats, setVats] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [lineItems, setLineItems] = useState([{
    id: 1, description: '', account_id: '', account_code: '', account_name: '',
    amount: 0, vat_id: '', vat_code: '', tax: 0, vat_amount: 0, total: 0,
    paid_by: 0, category_id: '', quantity: 1, unit_price: 0,
  }]);

  const pendingAccountDataRef = useRef({});
  const [attachmentDialogRow, setAttachmentDialogRow] = useState(null);

  const isReadOnly = !isNew && expenseState !== EXPENSE_STATE.DRAFT;

  const { register, handleSubmit, control, setValue, trigger, watch, formState: { errors } } = useForm({
    defaultValues: {
      description: '',
      date: new Date().toISOString().split('T')[0],
      employee: null,
      manager: null,
      notes: '',
    },
  });

  const descriptionValue = watch('description');

  useEffect(() => {
    return () => {
      if (setBreadcrumbTitle) {
        setBreadcrumbTitle(null);
      }
    };
  }, [setBreadcrumbTitle]);

  useEffect(() => {
    if (tenantId) {
      fetchEmployees();
      fetchVATs();
      fetchCategories();
      fetchAccounts();
      if (!isNew) {
        fetchExpense();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id]);

  const fetchEmployees = async () => {
    try {
      const response = await employeesAPI.getAll(tenantId);
      const emps = response.data.employees || [];
      setEmployees(emps);

      // Default to current user's employee record for new expenses
      if (isNew && user?.user_id) {
        const myEmployee = emps.find(emp => emp.user_id === user.user_id);
        if (myEmployee) {
          setValue('employee', { id: myEmployee.id, name: myEmployee.name });
          if (myEmployee.manager_id) {
            const mgr = emps.find(emp => emp.id === myEmployee.manager_id);
            if (mgr) {
              setValue('manager', { id: mgr.id, name: mgr.name });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchVATs = async () => {
    try {
      const response = await vatsAPI.getAll(tenantId, { limit: 1000, state: 0 });
      setVats(response.data.vats || []);
    } catch (error) {
      console.error('Error fetching VATs:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll(tenantId);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await coaAPI.getAll(tenantId, { limit: 1000, state: 2 });
      const coaList = response.data.charts_of_accounts || [];
      setAccounts(flattenAccounts(coaList));
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const fetchExpense = async () => {
    try {
      setLoading(true);
      const response = await expensesAPI.getOne(tenantId, id);
      const exp = response.data;

      setValue('description', exp.description || '');
      setValue('date', exp.date || '');
      setValue('notes', exp.notes || '');

      if (exp.employee_id) {
        setValue('employee', { id: exp.employee_id, name: exp.employee_name || '' });
      }
      if (exp.manager_id) {
        setValue('manager', { id: exp.manager_id, name: exp.manager_name || '' });
      }

      setExpenseState(exp.state ?? EXPENSE_STATE.DRAFT);

      if (exp.rows && exp.rows.length > 0) {
        setLineItems(exp.rows.map((row, idx) => ({
          id: row.id || idx + 1,
          description: row.description || '',
          account_id: row.account_id || '',
          account_code: row.account_code || '',
          account_name: row.account_name || '',
          amount: parseFloat(row.amount) || 0,
          vat_id: row.vat_id || '',
          vat_code: row.vat_code || '',
          tax: 0,
          vat_amount: parseFloat(row.vat_amount) || 0,
          total: parseFloat(row.total) || 0,
          paid_by: row.paid_by ?? 0,
          category_id: row.category_id || '',
          quantity: parseFloat(row.quantity) || 1,
          unit_price: parseFloat(row.unit_price) || 0,
          attachment_count: row.attachment_count || 0,
        })));
      }

      if (setBreadcrumbTitle) {
        setBreadcrumbTitle(exp.description || `#${exp.number}`);
      }
    } catch (error) {
      console.error('Error fetching expense:', error);
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const flattenAccounts = (accountList, result = []) => {
    for (const account of accountList) {
      result.push(account);
      if (account.children && account.children.length > 0) {
        flattenAccounts(account.children, result);
      }
    }
    return result;
  };

  const fetchAccountOptions = useCallback(async (search) => {
    if (!tenantId) return [];
    const response = await coaAPI.getAll(tenantId, { limit: 50, state: 2, search: search || undefined });
    const coaList = response.data.charts_of_accounts || [];
    return flattenAccounts(coaList).map((a) => ({ value: a.id, label: `${a.code} - ${a.name}`, data: a }));
  }, [tenantId]);

  const handleAccountSelect = useCallback((rowId, option) => {
    pendingAccountDataRef.current[rowId] = {
      account_code: option.data?.code || null,
      account_name: option.data?.name || null,
      account_vat: option.data?.vat || null,
    };
  }, []);

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.total || 0), 0).toFixed(2);
  };

  const addLineItem = () => {
    const newId = lineItems.length > 0 ? Math.max(...lineItems.map(item => item.id)) + 1 : 1;
    setLineItems([...lineItems, {
      id: newId, description: '', account_id: '', account_code: '', account_name: '',
      amount: 0, vat_id: '', vat_code: '', tax: 0, vat_amount: 0, total: 0,
      paid_by: 0, category_id: '', quantity: 1, unit_price: 0,
    }]);
  };

  const deleteLineItem = useCallback((itemId) => {
    setLineItems(prev => {
      if (prev.length > 1) {
        return prev.filter(item => item.id !== itemId);
      }
      return prev;
    });
  }, []);

  const handleMobileRowUpdate = useCallback((updatedRow) => {
    setLineItems(prev => prev.map(item => item.id === updatedRow.id ? updatedRow : item));
  }, []);

  const processRowUpdate = useCallback((newRow, oldRow) => {
    let updatedRow = { ...newRow };
    let accountVatApplied = false;

    // Apply pending account data from ServerFilteredEditCell
    if (pendingAccountDataRef.current[newRow.id]) {
      const pendingData = pendingAccountDataRef.current[newRow.id];
      const accountVatId = pendingData.account_vat;
      delete pendingData.account_vat;
      updatedRow = { ...updatedRow, ...pendingData };
      delete pendingAccountDataRef.current[newRow.id];

      if (accountVatId && newRow.account_id !== oldRow.account_id) {
        const accountVat = vats.find(v => v.id === accountVatId);
        if (accountVat) {
          updatedRow.vat_id = accountVat.id;
          updatedRow.tax = parseFloat(accountVat.percent);
          updatedRow.vat_code = accountVat.code;
          accountVatApplied = true;
        }
      }
    }

    // When category changes, set unit_price from category
    if (newRow.category_id !== oldRow.category_id) {
      const cat = categories.find(c => c.id === newRow.category_id);
      if (cat) {
        updatedRow.unit_price = parseFloat(cat.unit_price) || 0;
        // If unit_price > 0 (e.g. mileage), compute amount = quantity * unit_price
        if (updatedRow.unit_price > 0) {
          updatedRow.amount = parseFloat(((updatedRow.quantity || 1) * updatedRow.unit_price).toFixed(2));
          const tax = updatedRow.tax || 0;
          updatedRow.vat_amount = parseFloat(((updatedRow.amount) * tax / 100).toFixed(2));
          updatedRow.total = parseFloat(((updatedRow.amount) + updatedRow.vat_amount).toFixed(2));
          setLineItems(prev => prev.map(item => item.id === newRow.id ? updatedRow : item));
          return updatedRow;
        }
      } else {
        updatedRow.unit_price = 0;
      }
    }

    // When quantity changes and unit_price > 0, recalculate amount
    if (newRow.quantity !== oldRow.quantity && updatedRow.unit_price > 0) {
      updatedRow.amount = parseFloat(((updatedRow.quantity || 1) * updatedRow.unit_price).toFixed(2));
      const tax = updatedRow.tax || 0;
      updatedRow.vat_amount = parseFloat(((updatedRow.amount) * tax / 100).toFixed(2));
      updatedRow.total = parseFloat(((updatedRow.amount) + updatedRow.vat_amount).toFixed(2));
      setLineItems(prev => prev.map(item => item.id === newRow.id ? updatedRow : item));
      return updatedRow;
    }

    // Update tax when vat_id changes explicitly
    if (newRow.vat_id !== oldRow.vat_id && !accountVatApplied) {
      const vat = vats.find(v => v.id === newRow.vat_id);
      if (vat) {
        updatedRow.tax = parseFloat(vat.percent);
        updatedRow.vat_code = vat.code;
      }
    }

    const tax = updatedRow.tax || 0;

    // If amount changed, recalculate vat_amount and total
    if (newRow.amount !== oldRow.amount) {
      updatedRow.vat_amount = parseFloat(((updatedRow.amount || 0) * tax / 100).toFixed(2));
      updatedRow.total = parseFloat(((updatedRow.amount || 0) + updatedRow.vat_amount).toFixed(2));
    // If VAT changed, recalculate from amount
    } else if (newRow.vat_id !== oldRow.vat_id || accountVatApplied) {
      updatedRow.vat_amount = parseFloat(((updatedRow.amount || 0) * tax / 100).toFixed(2));
      updatedRow.total = parseFloat(((updatedRow.amount || 0) + updatedRow.vat_amount).toFixed(2));
    // If total changed directly, back-calculate amount
    } else if (newRow.total !== oldRow.total) {
      updatedRow.amount = parseFloat(((updatedRow.total || 0) / (1 + tax / 100)).toFixed(2));
      updatedRow.vat_amount = parseFloat(((updatedRow.total || 0) - updatedRow.amount).toFixed(2));
    }

    setLineItems(prev => prev.map(item => item.id === newRow.id ? updatedRow : item));
    return updatedRow;
  }, [vats, categories]);

  const paidByOptions = useMemo(() => [
    { value: 0, label: t('expenses.paid_by_employee') },
    { value: 1, label: t('expenses.paid_by_company') },
  ], [t]);

  const categoryOptions = useMemo(() =>
    categories.map(c => ({ value: c.id, label: `${c.code} - ${c.name}` })),
    [categories]
  );

  const columns = useMemo(() => [
    {
      field: 'category_id',
      headerName: t('expenses.row.category'),
      width: 180,
      editable: !isReadOnly,
      type: 'singleSelect',
      valueOptions: categoryOptions,
    },
    {
      field: 'description',
      headerName: t('expenses.row.description'),
      flex: 1,
      editable: !isReadOnly,
    },
    {
      field: 'account_id',
      headerName: t('expenses.row.account'),
      width: 250,
      editable: !isReadOnly,
      renderEditCell: (params) => (
        <ServerFilteredEditCell {...params} fetchOptions={fetchAccountOptions} onSelect={handleAccountSelect} />
      ),
      valueFormatter: (value, row) => {
        if (!row?.account_code) return '';
        return `${row.account_code} - ${row.account_name}`;
      },
    },
    {
      field: 'quantity',
      headerName: t('expenses.row.quantity'),
      width: 90,
      editable: !isReadOnly,
      type: 'number',
      valueParser: (value) => Number(value) || 0,
    },
    {
      field: 'unit_price',
      headerName: t('expenses.row.unit_price'),
      width: 110,
      editable: false,
      type: 'number',
      valueFormatter: (value) => `${(value || 0).toFixed(2)} \u20AC`,
    },
    {
      field: 'amount',
      headerName: t('expenses.row.amount'),
      width: 120,
      editable: !isReadOnly,
      type: 'number',
      valueParser: (value) => Number(value) || 0,
      valueFormatter: (value) => `${(value || 0).toFixed(2)} \u20AC`,
    },
    {
      field: 'vat_id',
      headerName: t('expenses.row.vat'),
      width: 140,
      editable: !isReadOnly,
      type: 'singleSelect',
      valueOptions: vats.map(v => ({ value: v.id, label: `${v.percent}% (${v.code})` })),
      valueFormatter: (value, row) => {
        if (!row?.vat_code) return '';
        return `${row.tax}% (${row.vat_code})`;
      },
    },
    {
      field: 'vat_amount',
      headerName: t('expenses.row.vat_amount'),
      width: 110,
      editable: false,
      type: 'number',
      valueFormatter: (value) => `${(value || 0).toFixed(2)} \u20AC`,
    },
    {
      field: 'total',
      headerName: t('expenses.row.total'),
      width: 120,
      editable: !isReadOnly,
      type: 'number',
      valueParser: (value) => Number(value) || 0,
      valueFormatter: (value) => `${(value || 0).toFixed(2)} \u20AC`,
    },
    {
      field: 'paid_by',
      headerName: t('expenses.row.paid_by'),
      width: 160,
      editable: !isReadOnly,
      type: 'singleSelect',
      valueOptions: paidByOptions,
    },
    {
      field: 'attachment_count',
      headerName: '',
      width: 50,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        if (!params.row.id || typeof params.row.id !== 'string') return null;
        const count = params.row.attachment_count || 0;
        return (
          <IconButton size="small" onClick={() => setAttachmentDialogRow(params.row)}>
            <Badge badgeContent={count} color="primary" overlap="circular">
              <AttachFile fontSize="small" />
            </Badge>
          </IconButton>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      width: 50,
      getActions: (params) => (lineItems.length > 1 && !isReadOnly) ? [
        <GridActionsCellItem
          key="delete"
          icon={<Delete />}
          label="Delete"
          onClick={() => deleteLineItem(params.id)}
        />
      ] : [],
    },
  ], [vats, categories, categoryOptions, paidByOptions, fetchAccountOptions, handleAccountSelect, t, isReadOnly, lineItems.length, deleteLineItem]);

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      const payload = {
        description: data.description.trim(),
        date: data.date,
        employee_id: data.employee?.id || null,
        employee_name: data.employee?.name || null,
        manager_id: data.manager?.id || null,
        manager_name: data.manager?.name || null,
        notes: data.notes || null,
        rows: lineItems.map(item => ({
          description: item.description || '',
          account_id: item.account_id || null,
          amount: item.amount || 0,
          vat_id: item.vat_id || null,
          vat_amount: item.vat_amount || 0,
          total: item.total || 0,
          paid_by: item.paid_by ?? 0,
          category_id: item.category_id || null,
          quantity: item.quantity ?? 1,
          unit_price: item.unit_price ?? 0,
        })),
      };

      if (isNew) {
        const response = await expensesAPI.create(tenantId, payload);
        showSuccess(t('expenses.created'));
        navigate(`/expenses/${response.data.id}`);
      } else {
        // Update header
        const { rows, ...headerPayload } = payload;
        await expensesAPI.update(tenantId, id, headerPayload);

        // Sync rows: for existing expense, use row API endpoints
        const existingResponse = await expensesAPI.getOne(tenantId, id);
        const existingRows = existingResponse.data.rows || [];
        const existingRowIds = new Set(existingRows.map(r => r.id));
        const currentRowIds = new Set(lineItems.filter(r => typeof r.id === 'string').map(r => r.id));

        // Delete removed rows
        for (const existingRow of existingRows) {
          if (!currentRowIds.has(existingRow.id)) {
            await expensesAPI.deleteRow(tenantId, id, existingRow.id);
          }
        }

        // Add or update rows
        for (const item of lineItems) {
          const rowData = {
            description: item.description || '',
            account_id: item.account_id,
            amount: item.amount || 0,
            vat_id: item.vat_id || null,
            vat_amount: item.vat_amount || 0,
            total: item.total || 0,
            paid_by: item.paid_by ?? 0,
            category_id: item.category_id || null,
            quantity: item.quantity ?? 1,
            unit_price: item.unit_price ?? 0,
          };

          if (typeof item.id === 'string' && existingRowIds.has(item.id)) {
            await expensesAPI.updateRow(tenantId, id, item.id, rowData);
          } else {
            await expensesAPI.addRow(tenantId, id, rowData);
          }
        }

        showSuccess(t('expenses.updated'));
        fetchExpense();
      }
    } catch (error) {
      console.error('Error saving expense:', error);
      handleApiError(error, t);
    } finally {
      setSaving(false);
    }
  };

  const onSubmitError = (formErrors) => {
    const errorMessages = [];
    Object.entries(formErrors).forEach(([, error]) => {
      if (error?.message) {
        errorMessages.push(error.message);
      }
    });
    if (errorMessages.length > 0) {
      showError(errorMessages.join('\n'));
    }
  };

  const handleSaveClick = async (e) => {
    if (e) e.preventDefault();
    const isValid = await trigger();
    if (!isValid) {
      onSubmitError(errors);
      return;
    }
    handleSubmit(onSubmit, onSubmitError)(e);
  };

  const handleCancel = () => {
    navigate('/expenses');
  };

  // Workflow actions
  const handleWorkflowAction = async (action, successMsg) => {
    try {
      setActionLoading(true);
      await action(tenantId, id);
      showSuccess(t(successMsg));
      fetchExpense();
    } catch (error) {
      console.error('Workflow action error:', error);
      handleApiError(error, t);
    } finally {
      setActionLoading(false);
    }
  };

  const getWorkflowActions = () => {
    if (isNew) return [];
    const actions = [];

    if (expenseState === EXPENSE_STATE.DRAFT) {
      actions.push({
        label: actionLoading ? t('expenses.action.submitting') : t('expenses.action.submit'),
        icon: <Send />,
        onClick: () => handleWorkflowAction(expensesAPI.submit, 'expenses.submitted'),
        disabled: actionLoading || saving,
        color: 'primary',
      });
    }

    if (expenseState === EXPENSE_STATE.SUBMITTED) {
      actions.push({
        label: actionLoading ? t('expenses.action.approving') : t('expenses.action.approve'),
        icon: <CheckCircle />,
        onClick: () => handleWorkflowAction(expensesAPI.approve, 'expenses.approved'),
        disabled: actionLoading || saving,
        color: 'success',
      });
      actions.push({
        label: actionLoading ? t('expenses.action.refusing') : t('expenses.action.refuse'),
        icon: <Block />,
        onClick: () => handleWorkflowAction(expensesAPI.refuse, 'expenses.refused'),
        disabled: actionLoading || saving,
        color: 'error',
      });
    }

    if (expenseState === EXPENSE_STATE.APPROVED) {
      actions.push({
        label: actionLoading ? t('expenses.action.posting') : t('expenses.action.post'),
        icon: <PostAdd />,
        onClick: () => handleWorkflowAction(expensesAPI.post, 'expenses.posted'),
        disabled: actionLoading || saving,
        color: 'primary',
      });
    }

    if (expenseState === EXPENSE_STATE.SUBMITTED || expenseState === EXPENSE_STATE.REFUSED) {
      actions.push({
        label: actionLoading ? t('expenses.action.resetting') : t('expenses.action.reset'),
        icon: <Undo />,
        onClick: () => handleWorkflowAction(expensesAPI.reset, 'expenses.reset'),
        disabled: actionLoading || saving,
      });
    }

    return actions;
  };

  if (!tenantId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('expenses.select_tenant')}</Alert>
      </Box>
    );
  }

  const activeStep = getActiveStep(expenseState);

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column', pb: '60px' }}>
      {/* Status bar */}
      <Paper sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, px: 3, py: 1, borderRadius: 0 }}>
        {!isNew && expenseState !== EXPENSE_STATE.REFUSED && (
          <Stepper activeStep={activeStep} sx={{ flex: 1, maxWidth: 500 }} alternativeLabel>
            {workflowSteps.map((step) => (
              <Step key={step.state}>
                <StepLabel>{t(`expenses.state.${step.label}`)}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
        {expenseState === EXPENSE_STATE.REFUSED && (
          <Typography color="error" variant="body2" sx={{ fontWeight: 500 }}>
            {t('expenses.state.refused')}
          </Typography>
        )}
      </Paper>

      {/* Main form content */}
      <Paper sx={{ display: 'flex', flexDirection: 'column', flex: 1, borderRadius: 0, overflow: 'hidden' }}>
        {/* Basic Info */}
        <Box sx={{ px: 3, pt: 2, pb: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label={t('expenses.description')}
                {...register('description', { required: t('expenses.error.description_required') })}
                error={!!errors.description}
                helperText={errors.description?.message}
                required
                disabled={loading || isReadOnly}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField
                fullWidth
                label={t('expenses.date')}
                {...register('date', { required: t('expenses.error.date_required') })}
                error={!!errors.date}
                helperText={errors.date?.message}
                required
                disabled={loading || isReadOnly}
                type="date"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Controller
                name="employee"
                control={control}
                rules={{ required: t('expenses.error.employee_required') }}
                render={({ field }) => (
                  <Autocomplete
                    size="small"
                    options={employees}
                    getOptionLabel={(option) => option.name || ''}
                    value={field.value}
                    onChange={(e, val) => field.onChange(val)}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('expenses.employee')}
                        required
                        error={!!errors.employee}
                        helperText={errors.employee?.message}
                      />
                    )}
                    disabled={loading || isReadOnly}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Controller
                name="manager"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    size="small"
                    options={employees.filter(emp => employees.some(other => other.manager_id === emp.id))}
                    getOptionLabel={(option) => option.name || ''}
                    value={field.value}
                    onChange={(e, val) => field.onChange(val)}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField {...params} label={t('expenses.manager')} />
                    )}
                    disabled={loading || isReadOnly}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12 }}>
              <TextField
                fullWidth
                label={t('expenses.notes')}
                {...register('notes')}
                disabled={loading || isReadOnly}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Tabs: Rows | Comments */}
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentTab}
            onChange={(e, v) => setCurrentTab(v)}
            sx={{ px: 3, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
          >
            <Tab label={t('expenses.rows', { defaultValue: 'Rows' })} />
            {!isNew && <Tab label={t('comments.title', { defaultValue: 'Comments' })} />}
          </Tabs>
        </Box>

        {/* Expense Rows - fills remaining space */}
        {currentTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {isMobile ? (
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {lineItems.map((row, index) => (
                  <ExpenseRowCard
                    key={row.id}
                    row={row}
                    index={index}
                    categories={categories}
                    accounts={accounts}
                    vats={vats}
                    onUpdate={handleMobileRowUpdate}
                    onDelete={deleteLineItem}
                    onAttach={(r) => typeof r.id === 'string' && setAttachmentDialogRow(r)}
                    canDelete={lineItems.length > 1}
                    readOnly={isReadOnly}
                  />
                ))}
              </Box>
            ) : (
              <Box sx={{ flex: 1, width: '100%', minHeight: 0 }}>
                <EditableDataGrid
                  rows={lineItems}
                  columns={columns}
                  editMode="cell"
                  processRowUpdate={processRowUpdate}
                  onProcessRowUpdateError={(error) => console.error('Row update error:', error)}
                  hideFooter
                  disableColumnMenu
                  disableColumnFilter
                  disableColumnSelector
                  disableRowSelectionOnClick
                  getRowId={(row) => row.id}
                  onAddRow={!isReadOnly ? addLineItem : undefined}
                  sx={{ border: 0 }}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 1, borderTop: 1, borderColor: 'divider' }}>
              <Button startIcon={<Add />} onClick={addLineItem} size="small" disabled={isReadOnly}>
                {t('expenses.add_row')}
              </Button>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {t('expenses.total')}: {calculateTotal()} &euro;
              </Typography>
            </Box>
          </Box>
        )}

        {currentTab === 1 && !isNew && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, px: 2, pb: 2 }}>
            <CommentsPanel
              tenantId={tenantId}
              resourceType="expense"
              resourceId={id}
              currentUserId={user?.user_id}
            />
          </Box>
        )}
      </Paper>

      <FormBottomBar
        onSave={handleSaveClick}
        onCancel={handleCancel}
        saving={saving}
        disabled={!descriptionValue?.trim()}
        saveDisabled={isReadOnly}
        actions={getWorkflowActions()}
      />

      <FormErrorDialog
        open={errorDialogOpen}
        onClose={closeDialog}
        message={formError?.message}
      />

      {attachmentDialogRow && (
        <AttachmentDialog
          open={!!attachmentDialogRow}
          onClose={() => setAttachmentDialogRow(null)}
          tenantId={tenantId}
          rowId={attachmentDialogRow.id}
          readOnly={isReadOnly}
          onCountChange={(count) => {
            setLineItems(prev =>
              prev.map(item =>
                item.id === attachmentDialogRow.id ? { ...item, attachment_count: count } : item
              )
            );
          }}
        />
      )}
    </Box>
  );
}
